<template>
  <el-dialog
    title="发起会话"
    :visible.sync="innerVisible"
    width="480px"
    append-to-body
    custom-class="start-chat-dialog"
    @close="onClose"
  >
    <!-- 发信号：多号时选择；单号直接展示；无号不可发起 -->
    <div v-if="accounts.length > 1" class="send-account-row">
      <span class="row-label">发信号</span>
      <el-select v-model="selectedVID" size="small" class="account-select">
        <el-option
          v-for="acc in accounts"
          :key="acc.vid"
          :label="acc.userName || acc.vid"
          :value="acc.vid"
        >
          <span>{{ acc.userName || acc.vid }}</span>
          <span v-if="!acc.login" class="offline-tag">离线</span>
        </el-option>
      </el-select>
    </div>
    <div v-else-if="accounts.length === 1" class="send-account-row">
      <span class="row-label">发信号</span>
      <span class="single-account">{{ accounts[0].userName || accounts[0].vid }}</span>
    </div>
    <div v-else class="send-account-row">
      <span class="row-label">发信号</span>
      <span class="no-account">暂无可用企微账号</span>
    </div>

    <el-tabs v-model="activeTab" @tab-click="onTabChange">
      <el-tab-pane label="内部同事" name="internal"></el-tab-pane>
      <el-tab-pane label="企微好友" name="wxwork"></el-tab-pane>
    </el-tabs>

    <el-input
      v-model="keyword"
      size="small"
      placeholder="搜索昵称"
      clearable
      prefix-icon="el-icon-search"
      @input="onKeywordInput"
    ></el-input>

    <div class="target-list" @scroll="onScroll">
      <div v-if="loading && list.length === 0" class="list-tip">加载中...</div>
      <div v-else-if="list.length === 0" class="list-tip">
        {{ accounts.length === 0 ? '暂无可用企微账号，无法发起会话' : '暂无匹配的联系人' }}
      </div>
      <div
        v-for="t in list"
        :key="t.userID"
        class="target-item"
        @click="onSelect(t)"
      >
        <el-avatar :size="34" :src="t.avatar">{{ (t.nickname || '?').charAt(0) }}</el-avatar>
        <div class="target-info">
          <div class="target-name">{{ t.nickname || t.userID }}</div>
          <div v-if="accounts.length > 1" class="target-sub">发起账号：{{ accountName(t.vid) }}</div>
        </div>
      </div>
      <div v-if="hasMore" class="list-tip more">{{ loading ? '加载中...' : '滚动加载更多' }}</div>
    </div>

    <div slot="footer" class="dialog-footer-tip">
      选择联系人后将打开会话，发送第一条消息后会话会保存并出现在列表中
    </div>
  </el-dialog>
</template>

<script>
import { getMyWxworkAccounts, getInternalContacts, getWxworkFriends } from '@/api/startChat.js'
import { buildSingleConvID } from '@/utils/conversation.js'
// store 为 default export（defineStore），必须 default import：
// named import 在 webpack 中仅告警不报错，运行时会得到 undefined 导致调用抛错
import useUserStore from '@/store/userStore.js'
import useChatStore from '@/store/chatStore.js'

// 企微对企微（internal）主动发起新会话：
// 选目标（内部同事/企微好友）+ 选发信号（多号时）→ 构造 convID 打开本地会话（时间线为空）
// → 首条消息经 send_message4 落库（后端会话不存在时按双 1688 autoCreate internal 会话）
export default {
  name: 'StartChatDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      accounts: [],       // 我的企微账号（选号优先级顺序）
      selectedVID: '',    // 发信号
      activeTab: 'internal',
      keyword: '',
      list: [],
      page: 1,
      pageSize: 100,
      total: 0,
      hasMore: false,
      loading: false,
      searchTimer: null,
      reqSeq: 0 // 请求序列号：条件变更后仅接受最新序列的响应，防旧结果覆盖
    }
  },
  computed: {
    innerVisible: {
      get() {
        return this.visible
      },
      set(v) {
        this.$emit('update:visible', v)
      }
    },
    tenantID() {
      return useUserStore().userInfo.tenantID || 1
    }
  },
  watch: {
    visible(v) {
      if (v) {
        this.onOpen()
      }
    }
  },
  methods: {
    accountName(vid) {
      const acc = this.accounts.find(a => a.vid === vid)
      return acc ? (acc.userName || acc.vid) : vid
    },
    async onOpen() {
      // 账号列表（每次打开刷新，感知登录态/新增账号变化）
      this.accounts = []
      this.selectedVID = ''
      try {
        const accounts = await getMyWxworkAccounts()
        this.accounts = accounts || []
        if (this.accounts.length > 0) {
          this.selectedVID = this.accounts[0].vid
        }
      } catch (e) {
        // 功能未开启等错误已由全局拦截器提示
      }
      this.activeTab = 'internal'
      this.resetAndLoad()
    },
    onClose() {
      clearTimeout(this.searchTimer)
      this.keyword = ''
      this.list = []
    },
    onTabChange() {
      this.resetAndLoad()
    },
    onKeywordInput() {
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => {
        this.resetAndLoad()
      }, 300)
    },
    async resetAndLoad() {
      // 条件变更（打开/Tab/搜索）：序列号自增，旧请求响应将被丢弃；page 从 1 重新开始。
      // 必须先复位 loading：否则上一序列在飞请求（loading=true）会把新序列请求拦在
      // loadPage 的同序列守卫之外，且旧响应因 seq 不匹配不会复位 loading → 永久卡“加载中”
      this.reqSeq = (this.reqSeq || 0) + 1
      this.loading = false
      this.page = 1
      this.list = []
      this.total = 0
      this.hasMore = false
      await this.loadPage(this.reqSeq, 1)
    },
    async loadPage(seq, page) {
      // 同序列并发去重；新序列（条件变更）强制发起
      if (this.loading && seq === this.reqSeq) {
        return
      }
      const tab = this.activeTab
      const keyword = this.keyword
      this.loading = true
      try {
        const params = { keyword, page, pageSize: this.pageSize }
        const res = tab === 'internal'
          ? await getInternalContacts(params)
          : await getWxworkFriends(params)
        // 过期响应：条件已变更（搜索词/Tab/重新打开），丢弃不写入
        if (seq !== this.reqSeq) {
          return
        }
        const items = (res && res.list) || []
        this.list = page === 1 ? items : this.list.concat(items)
        this.total = (res && res.total) || this.list.length
        this.page = page
        this.hasMore = this.list.length < this.total
      } catch (e) {
        // 错误提示由全局拦截器处理；page 不推进 → 下次滚动重试该页，避免列表永久缺口
      } finally {
        if (seq === this.reqSeq) {
          this.loading = false
        }
      }
    },
    onScroll(e) {
      const el = e.target
      if (el.scrollHeight - el.scrollTop - el.clientHeight < 60 && this.hasMore && !this.loading) {
        this.loadPage(this.reqSeq, this.page + 1)
      }
    },
    onSelect(t) {
      if (!t || !t.userID) {
        return
      }
      // 发起账号跟随候选的持有账号（t.vid 为拥有该联系人且优先级最高的我的账号）：
      // 多号时若用户所选号未添加该联系人，协议层可能无法投递；自动联动下拉并提示
      let useVID = this.selectedVID
      if (t.vid && t.vid !== this.selectedVID) {
        useVID = t.vid
        this.selectedVID = t.vid
      }
      if (!useVID) {
        this.$message.warning('暂无可用企微账号，无法发起会话')
        return
      }
      const peerVID = t.userID
      const conversationID = buildSingleConvID(this.tenantID, useVID, peerVID)
      const account = this.accounts.find(a => a.vid === useVID)
      const chatStore = useChatStore()
      chatStore.openChat({
        conversationID,
        convType: 'single',
        scene: 'internal',
        userID: peerVID,
        showName: t.nickname || peerVID,
        faceURL: t.avatar || '',
        corpUserID: useVID,
        personalUserID: peerVID,
        source: useVID,
        identity: account ? account.identity : '',
        // 发起时选定的发送账号：ChatBox 切换会话时继承（发送链路经 msgInfo.accountVID 透传）
        sendAccountVID: useVID,
        // 本地待落库标记：周期同步幽灵过滤豁免（仅本次页面会话内有效，
        // 刷新后未落库空会话由周期同步清理，与“发送第一条消息后会话会保存”口径一致）
        _localOnly: true
      })
      const chats = chatStore.findChats()
      const idx = chats.findIndex(c => c.conversationID === conversationID)
      if (idx >= 0) {
        chatStore.setActiveChat(idx)
      }
      this.innerVisible = false
      this.$message({
        type: 'success',
        message: '会话已打开，发送第一条消息后会话将保存',
        duration: 3000
      })
    }
  }
}
</script>

<style scoped>
.start-chat-dialog >>> .el-dialog__body {
  padding-top: 12px;
}
.send-account-row {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
}
.row-label {
  width: 56px;
  font-size: 13px;
  color: #606266;
  flex-shrink: 0;
}
.account-select {
  flex: 1;
}
.offline-tag {
  float: right;
  color: #f56c6c;
  font-size: 12px;
}
.single-account {
  font-size: 13px;
  color: #303133;
}
.no-account {
  font-size: 13px;
  color: #f56c6c;
}
.target-list {
  height: 320px;
  overflow-y: auto;
  margin-top: 10px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
}
.list-tip {
  padding: 24px 0;
  text-align: center;
  color: #909399;
  font-size: 13px;
}
.list-tip.more {
  padding: 10px 0;
}
.target-item {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  cursor: pointer;
}
.target-item:hover {
  background-color: #f5f7fa;
}
.target-info {
  margin-left: 10px;
  min-width: 0;
}
.target-name {
  font-size: 14px;
  color: #303133;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.target-sub {
  font-size: 12px;
  color: #909399;
}
.dialog-footer-tip {
  text-align: center;
  font-size: 12px;
  color: #909399;
}
</style>
